import { z } from 'zod';
import { programSchema } from '@vta/types';

/**
 * Root-route search params.
 *
 * Every facet of the homepage globe view is encodable as URL state so that:
 *   - `/?team=839A` deep-links into a team drawer + camera fly-to.
 *   - `/?country=canada&program=v5rc` shows a filtered view.
 *   - sharing the URL reproduces the same view in another tab.
 *
 * This schema is the source of truth — both the router and the Zustand
 * filter-store read/write it. Anything not in this schema does NOT belong in
 * URL state (e.g. drawer scroll position).
 */
export const rootSearchSchema = z.object({
  team: z.string().min(1).max(16).optional(),
  city: z.string().min(1).max(120).optional(),
  country: z.string().min(1).max(120).optional(),
  program: programSchema.optional(),
  division: z.string().min(1).max(120).optional(),
  event: z.string().min(1).max(120).optional(),
});

export type RootSearch = z.infer<typeof rootSearchSchema>;

/**
 * Parse loose router input into a RootSearch.
 *
 * Returns an empty object on parse failure so a malformed query string
 * never breaks navigation; the bad keys silently drop and `searchInvalid`
 * can be surfaced from the router if we later want a toast.
 */
export function parseRootSearch(raw: Record<string, unknown>): RootSearch {
  const result = rootSearchSchema.safeParse(raw);
  return result.success ? result.data : {};
}

/**
 * Normalize a partial update so undefined keys are dropped, not preserved as
 * `key=undefined`. TanStack Router omits keys whose value is undefined.
 */
export function mergeSearch(prev: RootSearch, next: Partial<RootSearch>): RootSearch {
  const merged: RootSearch = { ...prev, ...next };
  (Object.keys(merged) as Array<keyof RootSearch>).forEach((key) => {
    if (merged[key] === undefined || merged[key] === '') {
      delete merged[key];
    }
  });
  return merged;
}
